<template>
  <div>
    <div>
      <b-form inline class="mb-3">
        <b-form-input
          type="search"
          placeholder="Search..."
          v-model="search"
        ></b-form-input>
      </b-form>
    </div>

    <b-card-group deck class="models-deck">
      <b-card
        no-body
        v-for="model in filteredModels"
        :key="model.guid"
        @click.stop="model.selected = !model.selected"
        :class="model.selected ? 'selected' : ''">

        <b-checkbox
          inline
          plain
          name="model-checkbox"
          :key="model.guid"
          v-model="model.selected"
          class="card-select"
          @input="onModelToggled"
        />

        <b-button
          variant="secondary"
          size="sm"
          @click.stop="showViewer(model.guid, model.format)"
          class="view-model"
        >
          <b-icon-box></b-icon-box>
        </b-button>

        <b-card-img :ref="'thumbnail-' + model.guid" src=""></b-card-img>

        <b-card-body>
          <b-card-title>{{ model.name }}</b-card-title>
        </b-card-body>
        <b-card-body class="d-flex justify-content-between">
          <b-button
            :pressed="model.isActive"
            @click.stop="toggleModelActivation(model)"
            vaiant="primary"
            size="sm"
          >
            {{model.isActive ? 'Deactivate' : 'Activate' }}</b-button
          >
          <b-button
            variant="danger"
            size="sm"
            @click.stop="removeModel(model.guid)"
          >
            Remove
          </b-button>
        </b-card-body>
        <template #footer>
          <div class="d-flex justify-content-between">
            <small>{{model.isActive ? '🟢' : '🔴' }}</small>
            <small class="text-muted">{{
              formatDate(model.creationDate)
            }}</small>
            <small class="text-muted">{{ formatSize(model.fileSize) }}</small>
          </div>
        </template>
      </b-card>
    </b-card-group>

    <!-- View Model -->
    <b-modal
      :id="MODAL_ID_VIEW"
      title="Model Viewer"
      size="lg"
      centered
      role="dialog"
      modal-class="regular"
      :ok-only="true"
      ok-title="Close"
    >
      <template slot="default">
        <ViewModel
          :guid="viewedModelGuid"
          :format="viewedModelFormat"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import * as harpraApi from "@/services/api.service";
import * as formatUtils from "@/utils/format-utils";
import ViewModel from "@/components/Views/ViewModel";
// import { nextTick } from "process";
import { nextTick } from 'vue'

export default {
  name: "ModelsDeck",
  props: {
    storageType: String,
  },
  components: {
    ViewModel
  },
  data() {
    return {
      MODAL_ID_VIEW: "view-model-modal",
      search: "",
      models: [],
      thumbnails: [],
      viewedModelGuid: '',
      viewedModelFormat: 'fbx'
    };
  },
  computed: {
    filteredModels() {
      return this.models.filter((model) =>
        model.name.toLowerCase().includes(this.search.toLowerCase())
      );
    },
    selectedModels() {
      return this.models.filter(model => model.selected);
    }
  },
  watch: {
    filteredModels: function () {
      this.$nextTick(() => {
        this.fillImages();
      });
    },
  },
  methods: {
    formatDate: formatUtils.formatDate,
    formatSize: formatUtils.formatSize,
    async loadModels() {
      let downloadedModels = await harpraApi.getModelsAsync(this.storageType);

      this.models = downloadedModels.map(model => ({...model, selected: false}));

      this.$emit("models-loaded", this.models);
      this.$emit("models-toggled", this.selectedModels.length);
    },
    fillImages() {
      for (const model of this.filteredModels) {
        if (this.thumbnails[model.guid]) {
          this.setThumbnailSource(model.guid);
          continue;
        }

        harpraApi.getThumbnailAsync(model.guid).then((thumbnail) => {
          this.thumbnails[model.guid] = thumbnail;
          this.setThumbnailSource(model.guid);
        });
      }
    },
    setThumbnailSource(guid) {
      let imgRef = this.$refs["thumbnail-" + guid];

      if (!imgRef) {
        return;
      }

      imgRef[0].src = "data:image/jpeg;base64," + this.thumbnails[guid];
    },
    showViewer(guid, format) {
      this.$bvModal.show(this.MODAL_ID_VIEW);
      this.viewedModelGuid = guid;
      this.viewedModelFormat = format;
    },
    handleActivationDeactivationError(err) {
      this.$bvModal.msgBoxOk(
        "We couldn't complete the requested operation. Please make sure you're not exceeding your subscription limits.",
        {
          title: 'Operation Failed',
          modalClass: "regular",
          footerClass: 'p-2',
          headerBgVariant: 'danger'
        });
    },
    onModelToggled() {
      this.$emit("models-toggled", this.selectedModels.length);
    },
    async toggleModelActivation(model) {
      if (model.isActive) {
        await harpraApi.deactivateModelAsync(model.guid)
          .then(() => {
            model.isActive = false;
          }).catch(err => this.handleActivationDeactivationError(err));
      } else {
        await harpraApi.activateModelAsync(model.guid)
          .then(() => {
            model.isActive = true;
          }).catch(err => this.handleActivationDeactivationError(err));
      }

      this.$nextTick(() => {
        this.fillImages();
      });
    },
    removeModel(guid) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to remove this model?', {
        title: 'Remove model',
        size: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelVariant: "black",
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
        modalClass: "regular"
      }).then(async value => {
        if (!value) {
          return;
        }

        await harpraApi.deleteModel(guid);
        await this.loadModels();
      });
    },
    async removeSelectedModels() {
      const confirmed =
        await this.$bvModal.msgBoxConfirm('Are you sure you want to remove selected models?', {
          title: 'Remove selected models',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'YES',
          cancelVariant: "black",
          cancelTitle: 'NO',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
          modalClass: "regular"
        });

      if (confirmed) {
        console.log("Deleting..");

        let deletePromises =
          this.selectedModels.map(model => harpraApi.deleteModelAsync(model.guid));

        try {
          await Promise.all(deletePromises);
        } catch (error) {
          console.log("Something went wrong while deleting.")
        }

        console.log("Deleted.");

        await this.loadModels();
      }
    }
  },
  async created() {
    await this.loadModels();
  },
};
</script>
